import moment from 'moment-timezone';
import {
  createContext,
  FC,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import ServiceConfigKey from '../../constants/ServiceConfigKey';
import Loading from '../Loading';
import Maintenance from '../Maintenance';

import { useTimezoneQuery } from './Timezone.generated';

const defaultTimezone = 'UTC';

moment.tz.setDefault(defaultTimezone);

export const TimezoneContext = createContext<string>(defaultTimezone);

export const useTimezone = (): string => {
  const timezone = useContext(TimezoneContext);
  if (!timezone) {
    throw new Error('Unsupported timezone');
  }

  return timezone;
};

const TimezoneProvider: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const {
    previousData,
    data = previousData,
    loading,
    error,
  } = useTimezoneQuery();

  const timezone = useMemo(
    () =>
      data?.serviceConfigs?.find(
        ({ key }) =>
          (key as unknown as ServiceConfigKey) === ServiceConfigKey.Timezone,
      )?.value || defaultTimezone,
    [data?.serviceConfigs],
  );

  useEffect(() => {
    moment.tz.setDefault(timezone);
  }, [timezone]);

  if (error) {
    return <Maintenance />;
  }

  if (loading && !timezone) {
    return <Loading />;
  }

  return (
    <TimezoneContext.Provider value={timezone}>
      {children}
    </TimezoneContext.Provider>
  );
};

TimezoneProvider.displayName = 'TimezoneProvider';

export default TimezoneProvider;
